import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { X, Sparkles, Tag, Users, Calendar } from 'lucide-react'

import { supabase } from '../../config/supabase'
import toast from 'react-hot-toast'

const OfferFormModal = ({ editingOffer, onClose, onSuccess }) => {
 const [formData, setFormData] = useState({
 offer_code: '',
 offer_name: '',
 offer_type: 'discount',
 discount_type: 'percentage',
 discount_value: '',
 max_discount_amount: '',
 min_order_amount: '',
 usage_limit_per_customer: 1,
 total_usage_limit: '',
 is_active: true,
 valid_from: new Date().toISOString().split('T')[0],
 valid_until: '',
 description: '',
 terms_and_conditions: '',
 target_user_type: 'all',
 offer_category: 'general',
 priority: 0,
 auto_apply: false,
 display_title: '',
 display_subtitle: '',
 min_avg_order_value: '',
 max_avg_order_value: '',
 min_lifetime_spend: '',
 max_lifetime_spend: '',
 min_total_orders: '',
 max_total_orders: ''
 })

 useEffect(() => {
 if (editingOffer) {
 setFormData({
 offer_code: editingOffer.offer_code || '',
 offer_name: editingOffer.offer_name || '',
 offer_type: editingOffer.offer_type || 'discount',
 discount_type: editingOffer.discount_type || 'percentage',
 discount_value: editingOffer.discount_value || '',
 max_discount_amount: editingOffer.max_discount_amount || '',
 min_order_amount: editingOffer.min_order_amount || '',
 usage_limit_per_customer: editingOffer.usage_limit_per_customer || 1,
 total_usage_limit: editingOffer.total_usage_limit || '',
 is_active: editingOffer.is_active ?? true,
 valid_from: editingOffer.valid_from ? editingOffer.valid_from.split('T')[0] : '',
 valid_until: editingOffer.valid_until ? editingOffer.valid_until.split('T')[0] : '',
 description: editingOffer.description || '',
 terms_and_conditions: editingOffer.terms_and_conditions || '',
 target_user_type: editingOffer.target_user_type || 'all',
 offer_category: editingOffer.offer_category || 'general',
 priority: editingOffer.priority || 0,
 auto_apply: editingOffer.auto_apply || false,
 display_title: editingOffer.display_title || '',
 display_subtitle: editingOffer.display_subtitle || '',
 min_avg_order_value: editingOffer.min_avg_order_value || '',
 max_avg_order_value: editingOffer.max_avg_order_value || '',
 min_lifetime_spend: editingOffer.min_lifetime_spend || '',
 max_lifetime_spend: editingOffer.max_lifetime_spend || '',
 min_total_orders: editingOffer.min_total_orders || '',
 max_total_orders: editingOffer.max_total_orders || ''
 })
 }
 }, [editingOffer])

 const handleSubmit = async (e) => {
 e.preventDefault()

 try {
 if (!formData.offer_code || !formData.offer_name || !formData.discount_value) {
 toast.error('Please fill in all required fields')
 return
 }

 const offerData = {
 ...formData,
 discount_value: parseFloat(formData.discount_value),
 max_discount_amount: formData.max_discount_amount ? parseFloat(formData.max_discount_amount) : null,
 min_order_amount: formData.min_order_amount ? parseFloat(formData.min_order_amount) : null,
 usage_limit_per_customer: parseInt(formData.usage_limit_per_customer) || 1,
 total_usage_limit: formData.total_usage_limit ? parseInt(formData.total_usage_limit) : null,
 priority: parseInt(formData.priority) || 0,
 min_avg_order_value: formData.min_avg_order_value ? parseFloat(formData.min_avg_order_value) : 0,
 max_avg_order_value: formData.max_avg_order_value ? parseFloat(formData.max_avg_order_value) : null,
 min_lifetime_spend: formData.min_lifetime_spend ? parseFloat(formData.min_lifetime_spend) : 0,
 max_lifetime_spend: formData.max_lifetime_spend ? parseFloat(formData.max_lifetime_spend) : null,
 min_total_orders: formData.min_total_orders ? parseInt(formData.min_total_orders) : 0,
 max_total_orders: formData.max_total_orders ? parseInt(formData.max_total_orders) : null,
 // Fix timestamp fields - convert empty strings to null
 valid_from: formData.valid_from || null,
 valid_until: formData.valid_until || null
 }

 if (editingOffer) {
 const { error } = await supabase
 .from('offers')
 .update(offerData)
 .eq('id', editingOffer.id)

 if (error) throw error
 toast.success('Offer updated successfully!')
 } else {
 const { error } = await supabase
 .from('offers')
 .insert([offerData])

 if (error) throw error
 toast.success('Offer created successfully!')
 }

 onSuccess()
 } catch (error) {
 console.error('Error saving offer:', error)
 toast.error(error.message || 'Failed to save offer')
 }
 }

 return (
 <motion.div
 initial={{ opacity: 0 }}
 animate={{ opacity: 1 }}
 exit={{ opacity: 0 }}
 className="fixed inset-0 bg-slate-900 bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto"
 onClick={onClose}
 >
 <motion.div
 initial={{ scale: 0.9, y: 20 }}
 animate={{ scale: 1, y: 0 }}
 exit={{ scale: 0.9, y: 20 }}
 onClick={(e) => e.stopPropagation()}
 className="bg-white rounded-xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-slate-100 shadow-sm shadow-md my-8"
 >
 {/* Header */}
 <div className="flex justify-between items-center mb-6 pb-4 border-b border-slate-100">
 <h2 className="text-2xl font-semibold text-slate-900">
 {editingOffer ? 'Edit Offer' : 'Create New Offer'}
 </h2>
 <button onClick={onClose} className="p-2 hover:bg-neutral-100 rounded-lg transition-colors">
 <X className="h-6 w-6" />
 </button>
 </div>

 {/* Form */}
 <form onSubmit={handleSubmit} className="space-y-6">
 {/* Basic Information */}
 <div>
 <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
 <Sparkles className="h-5 w-5" />
 Basic Information
 </h3>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <div>
 <label className="block text-sm font-medium text-slate-900 mb-2">
 Offer Code <span className="text-red-500">*</span>
 </label>
 <input
 type="text"
 value={formData.offer_code}
 onChange={(e) => setFormData({ ...formData, offer_code: e.target.value.toUpperCase() })}
 className="w-full px-4 py-2 border border-slate-100 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500"
 placeholder="SUMMER2024"
 required
 />
 </div>

 <div>
 <label className="block text-sm font-medium text-slate-900 mb-2">
 Offer Name <span className="text-red-500">*</span>
 </label>
 <input
 type="text"
 value={formData.offer_name}
 onChange={(e) => setFormData({ ...formData, offer_name: e.target.value })}
 className="w-full px-4 py-2 border border-slate-100 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500"
 placeholder="Summer Sale"
 required
 />
 </div>

 <div>
 <label className="block text-sm font-medium text-slate-900 mb-2">Display Title</label>
 <input
 type="text"
 value={formData.display_title}
 onChange={(e) => setFormData({ ...formData, display_title: e.target.value })}
 className="w-full px-4 py-2 border border-slate-100 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500"
 placeholder="🌞 Summer Special - 20% OFF"
 />
 </div>

 <div>
 <label className="block text-sm font-medium text-slate-900 mb-2">Display Subtitle</label>
 <input
 type="text"
 value={formData.display_subtitle}
 onChange={(e) => setFormData({ ...formData, display_subtitle: e.target.value })}
 className="w-full px-4 py-2 border border-slate-100 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500"
 placeholder="Beat the heat with amazing discounts!"
 />
 </div>
 </div>
 </div>

 {/* Discount Configuration */}
 <div>
 <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
 <Tag className="h-5 w-5" />
 Discount Configuration
 </h3>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <div>
 <label className="block text-sm font-medium text-slate-900 mb-2">
 Discount Type <span className="text-red-500">*</span>
 </label>
 <select
 value={formData.discount_type}
 onChange={(e) => setFormData({ ...formData, discount_type: e.target.value })}
 className="w-full px-4 py-2 border border-slate-100 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500"
 >
 <option value="percentage">Percentage (%)</option>
 <option value="fixed">Fixed Amount (₹)</option>
 </select>
 </div>

 <div>
 <label className="block text-sm font-medium text-slate-900 mb-2">
 Discount Value <span className="text-red-500">*</span>
 </label>
 <input
 type="number"
 step="0.01"
 value={formData.discount_value}
 onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
 className="w-full px-4 py-2 border border-slate-100 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500"
 placeholder={formData.discount_type === 'percentage' ? '10' : '100'}
 required
 />
 </div>

 <div>
 <label className="block text-sm font-medium text-slate-900 mb-2">Max Discount Amount (₹)</label>
 <input
 type="number"
 step="0.01"
 value={formData.max_discount_amount}
 onChange={(e) => setFormData({ ...formData, max_discount_amount: e.target.value })}
 className="w-full px-4 py-2 border border-slate-100 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500"
 placeholder="50"
 />
 </div>

 <div>
 <label className="block text-sm font-medium text-slate-900 mb-2">Min Order Amount (₹)</label>
 <input
 type="number"
 step="0.01"
 value={formData.min_order_amount}
 onChange={(e) => setFormData({ ...formData, min_order_amount: e.target.value })}
 className="w-full px-4 py-2 border border-slate-100 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500"
 placeholder="299"
 />
 </div>
 </div>
 </div>

 {/* Target Audience */}
 <div>
 <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
 <Users className="h-5 w-5" />
 Target Audience
 </h3>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <div>
 <label className="block text-sm font-medium text-slate-900 mb-2">
 User Type <span className="text-red-500">*</span>
 </label>
 <select
 value={formData.target_user_type}
 onChange={(e) => setFormData({ ...formData, target_user_type: e.target.value })}
 className="w-full px-4 py-2 border border-slate-100 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500"
 >
 <option value="all">All Users</option>
 <option value="new_user">New Users Only</option>
 <option value="existing_user">Existing Users Only</option>
 <option value="guest_user">Guest Users Only</option>
 <option value="festive">Festive (All Users)</option>
 </select>
 </div>

 <div>
 <label className="block text-sm font-medium text-slate-900 mb-2">Offer Category</label>
 <select
 value={formData.offer_category}
 onChange={(e) => setFormData({ ...formData, offer_category: e.target.value })}
 className="w-full px-4 py-2 border border-slate-100 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500"
 >
 <option value="general">General</option>
 <option value="festive">Festive</option>
 <option value="seasonal">Seasonal</option>
 <option value="loyalty">Loyalty</option>
 <option value="welcome">Welcome</option>
 <option value="winback">Win-back</option>
 </select>
 </div>
 </div>
 </div>

 {/* Advanced Filters for Existing Users */}
 {formData.target_user_type === 'existing_user' && (
 <div className="bg-slate-50 p-4 rounded-lg border-2 border-indigo-200">
 <h3 className="text-lg font-semibold text-slate-900 mb-4">🎯 Existing User Filters</h3>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <div>
 <label className="block text-sm font-medium text-slate-900 mb-2">Min Avg Order Value (₹)</label>
 <input
 type="number"
 step="0.01"
 value={formData.min_avg_order_value}
 onChange={(e) => setFormData({ ...formData, min_avg_order_value: e.target.value })}
 className="w-full px-4 py-2 border border-slate-100 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500"
 placeholder="0"
 />
 </div>

 <div>
 <label className="block text-sm font-medium text-slate-900 mb-2">Max Avg Order Value (₹)</label>
 <input
 type="number"
 step="0.01"
 value={formData.max_avg_order_value}
 onChange={(e) => setFormData({ ...formData, max_avg_order_value: e.target.value })}
 className="w-full px-4 py-2 border border-slate-100 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500"
 placeholder="No limit"
 />
 </div>

 <div>
 <label className="block text-sm font-medium text-slate-900 mb-2">Min Lifetime Spend (₹)</label>
 <input
 type="number"
 step="0.01"
 value={formData.min_lifetime_spend}
 onChange={(e) => setFormData({ ...formData, min_lifetime_spend: e.target.value })}
 className="w-full px-4 py-2 border border-slate-100 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500"
 placeholder="0"
 />
 </div>

 <div>
 <label className="block text-sm font-medium text-slate-900 mb-2">Max Lifetime Spend (₹)</label>
 <input
 type="number"
 step="0.01"
 value={formData.max_lifetime_spend}
 onChange={(e) => setFormData({ ...formData, max_lifetime_spend: e.target.value })}
 className="w-full px-4 py-2 border border-slate-100 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500"
 placeholder="No limit"
 />
 </div>

 <div>
 <label className="block text-sm font-medium text-slate-900 mb-2">Min Total Orders</label>
 <input
 type="number"
 value={formData.min_total_orders}
 onChange={(e) => setFormData({ ...formData, min_total_orders: e.target.value })}
 className="w-full px-4 py-2 border border-slate-100 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500"
 placeholder="0"
 />
 </div>

 <div>
 <label className="block text-sm font-medium text-slate-900 mb-2">Max Total Orders</label>
 <input
 type="number"
 value={formData.max_total_orders}
 onChange={(e) => setFormData({ ...formData, max_total_orders: e.target.value })}
 className="w-full px-4 py-2 border border-slate-100 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500"
 placeholder="No limit"
 />
 </div>
 </div>
 <p className="text-sm text-neutral-600 mt-3">
 💡 These filters help target specific existing customers based on their spending patterns
 </p>
 </div>
 )}

 {/* Usage & Validity */}
 <div>
 <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
 <Calendar className="h-5 w-5" />
 Usage & Validity
 </h3>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <div>
 <label className="block text-sm font-medium text-slate-900 mb-2">Usage Per Customer</label>
 <input
 type="number"
 value={formData.usage_limit_per_customer}
 onChange={(e) => setFormData({ ...formData, usage_limit_per_customer: e.target.value })}
 className="w-full px-4 py-2 border border-slate-100 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500"
 placeholder="1"
 min="1"
 />
 </div>

 <div>
 <label className="block text-sm font-medium text-slate-900 mb-2">Total Usage Limit</label>
 <input
 type="number"
 value={formData.total_usage_limit}
 onChange={(e) => setFormData({ ...formData, total_usage_limit: e.target.value })}
 className="w-full px-4 py-2 border border-slate-100 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500"
 placeholder="Unlimited"
 />
 </div>

 <div>
 <label className="block text-sm font-medium text-slate-900 mb-2">Valid From</label>
 <input
 type="date"
 value={formData.valid_from}
 onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
 className="w-full px-4 py-2 border border-slate-100 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500"
 />
 </div>

 <div>
 <label className="block text-sm font-medium text-slate-900 mb-2">Valid Until</label>
 <input
 type="date"
 value={formData.valid_until}
 onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
 className="w-full px-4 py-2 border border-slate-100 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500"
 />
 </div>

 <div>
 <label className="block text-sm font-medium text-slate-900 mb-2">Priority (Display Order)</label>
 <input
 type="number"
 value={formData.priority}
 onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
 className="w-full px-4 py-2 border border-slate-100 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500"
 placeholder="0"
 />
 <p className="text-xs text-neutral-600 mt-1">Higher number = higher priority</p>
 </div>

 <div className="flex items-center gap-4">
 <label className="flex items-center gap-2 cursor-pointer">
 <input
 type="checkbox"
 checked={formData.is_active}
 onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
 className="h-5 w-5 text-white border border-slate-100 rounded focus:ring-2 focus:ring-indigo-500"
 />
 <span className="font-medium text-slate-900">Active</span>
 </label>

 <label className="flex items-center gap-2 cursor-pointer">
 <input
 type="checkbox"
 checked={formData.auto_apply}
 onChange={(e) => setFormData({ ...formData, auto_apply: e.target.checked })}
 className="h-5 w-5 text-white border border-slate-100 rounded focus:ring-2 focus:ring-indigo-500"
 />
 <span className="font-medium text-slate-900">Auto-Apply</span>
 </label>
 </div>
 </div>
 </div>

 {/* Description & Terms */}
 <div>
 <div className="grid grid-cols-1 gap-4">
 <div>
 <label className="block text-sm font-medium text-slate-900 mb-2">Description</label>
 <textarea
 value={formData.description}
 onChange={(e) => setFormData({ ...formData, description: e.target.value })}
 className="w-full px-4 py-2 border border-slate-100 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500"
 rows="3"
 placeholder="Brief description of the offer..."
 />
 </div>

 <div>
 <label className="block text-sm font-medium text-slate-900 mb-2">Terms & Conditions</label>
 <textarea
 value={formData.terms_and_conditions}
 onChange={(e) => setFormData({ ...formData, terms_and_conditions: e.target.value })}
 className="w-full px-4 py-2 border border-slate-100 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500"
 rows="3"
 placeholder="Terms and conditions..."
 />
 </div>
 </div>
 </div>

 {/* Submit Buttons */}
 <div className="flex gap-3 pt-4 border-t border-slate-100">
 <button
 type="button"
 onClick={onClose}
 className="flex-1 px-6 py-3 bg-neutral-200 hover:bg-neutral-300 text-slate-900 rounded-xl font-semibold border border-slate-100 transition-colors"
 >
 Cancel
 </button>
  <button
  type="submit"
  className="flex-1 px-6 py-3 bg-indigo-600 text-white rounded-xl font-semibold shadow-md hover:shadow-xl transition-all"
  >
  {editingOffer ? 'Update Offer' : 'Create Offer'}
  </button>

 </div>
 </form>
 </motion.div>
 </motion.div>
 )
}

export default OfferFormModal
